import { useState, useEffect } from "react";
import { Plus, AlertTriangle, Package, Edit, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const catColors = { "raw-material": "bg-amber-100 text-amber-700", "finished-goods": "bg-green-100 text-green-700", packaging: "bg-blue-100 text-blue-700" };
const emptyForm = { item_name: "", quantity: 0, unit: "kg", supplier: "", min_stock_level: 0, category: "raw-material" };

export default function InventoryManagement() {
  const { api, user } = useAuth();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const canEdit = ["owner", "manager", "supervisor", "hr_finance"].includes(user?.role);

  const load = () => { api().get("/inventory").then(r => setItems(r.data)).catch(() => {}); };
  useEffect(load, [api]);

  const lowStock = items.filter(i => i.quantity <= i.min_stock_level);

  const openEdit = (item) => { setEditId(item.id); setForm({ item_name: item.item_name, quantity: item.quantity, unit: item.unit, supplier: item.supplier, min_stock_level: item.min_stock_level, category: item.category }); setShowForm(true); };

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (editId) { await api().put(`/inventory/${editId}`, { quantity: form.quantity, supplier: form.supplier, min_stock_level: form.min_stock_level }); toast.success("Updated!"); }
      else { await api().post("/inventory", form); toast.success("Added!"); }
      setShowForm(false); setEditId(null); setForm(emptyForm); load();
    } catch { toast.error("Failed"); }
    setLoading(false);
  };

  return (
    <div className="space-y-6" data-testid="inventory-management">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div><h2 className="text-2xl font-serif text-navy">Inventory</h2><p className="text-slate-400 text-sm font-sans mt-1">{items.length} items tracked</p></div>
        {canEdit && <button onClick={() => { setEditId(null); setForm(emptyForm); setShowForm(!showForm); }} className="btn-primary flex items-center gap-2" data-testid="add-inventory-btn"><Plus className="w-5 h-5" />Add Item</button>}
      </div>

      {lowStock.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-testid="low-stock-alert">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800 font-sans">Low Stock Alert</p>
            <p className="text-sm text-amber-600 font-sans">{lowStock.map(i => i.item_name).join(", ")} are below minimum stock levels.</p>
          </div>
        </div>
      )}

      {showForm && (
        <div className="card-dashboard">
          <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-serif text-navy">{editId ? "Update" : "Add"} Item</h3><button onClick={() => { setShowForm(false); setEditId(null); }}><X className="w-5 h-5 text-slate-400" /></button></div>
          <form onSubmit={handleSubmit} className="grid md:grid-cols-3 gap-4" data-testid="inventory-form">
            <div><label className="form-label">Item Name</label><input type="text" value={form.item_name} onChange={e => setForm(f => ({ ...f, item_name: e.target.value }))} className="form-input" required disabled={!!editId} data-testid="inv-name" /></div>
            <div><label className="form-label">Quantity</label><input type="number" min="0" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: parseFloat(e.target.value) || 0 }))} className="form-input" data-testid="inv-qty" /></div>
            <div><label className="form-label">Unit</label><input type="text" value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} className="form-input" disabled={!!editId} data-testid="inv-unit" /></div>
            <div><label className="form-label">Supplier</label><input type="text" value={form.supplier} onChange={e => setForm(f => ({ ...f, supplier: e.target.value }))} className="form-input" data-testid="inv-supplier" /></div>
            <div><label className="form-label">Min Stock Level</label><input type="number" min="0" value={form.min_stock_level} onChange={e => setForm(f => ({ ...f, min_stock_level: parseFloat(e.target.value) || 0 }))} className="form-input" data-testid="inv-min-stock" /></div>
            <div><label className="form-label">Category</label><select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="form-input" disabled={!!editId} data-testid="inv-category"><option value="raw-material">Raw Material</option><option value="finished-goods">Finished Goods</option><option value="packaging">Packaging</option></select></div>
            <div className="md:col-span-3 flex justify-end gap-3">
              <button type="submit" disabled={loading} className="btn-primary text-sm" data-testid="save-inventory-btn">{loading ? "Saving..." : editId ? "Update" : "Add Item"}</button>
            </div>
          </form>
        </div>
      )}

      <div className="card-dashboard">
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="inventory-table">
            <thead><tr className="border-b border-slate-100">
              <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Item</th>
              <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Category</th>
              <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Quantity</th>
              <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Min Level</th>
              <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Supplier</th>
              <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Status</th>
              {canEdit && <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Actions</th>}
            </tr></thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`inv-row-${item.id}`}>
                  <td className="p-3 font-medium text-navy text-sm font-sans">{item.item_name}</td>
                  <td className="p-3"><span className={`px-2 py-0.5 text-xs font-medium font-sans ${catColors[item.category] || "bg-slate-100 text-slate-600"}`}>{(item.category || "").replace("-", " ")}</span></td>
                  <td className="p-3 font-mono text-sm">{item.quantity} {item.unit}</td>
                  <td className="p-3 font-mono text-sm text-slate-400">{item.min_stock_level} {item.unit}</td>
                  <td className="p-3 text-sm text-slate-600 font-sans">{item.supplier}</td>
                  <td className="p-3">
                    {item.quantity <= item.min_stock_level ? <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700">Low</span> : <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700">OK</span>}
                  </td>
                  {canEdit && (
                    <td className="p-3"><button onClick={() => openEdit(item)} className="p-2 hover:bg-blue-50 rounded-lg" data-testid={`edit-inv-${item.id}`}><Edit className="w-4 h-4 text-blue-500" /></button></td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
